// ─── Registre PUBLIC des noms (Core) ──────────────────────────────
// `users/{uid}` n'est lisible QUE par son propriétaire : impossible d'y lire le
// nom des AUTRES. Sans registre, tout écran qui liste des personnes hors de mon
// espace (accès accordé sur un élément, participant, auteur…) n'a que l'uid à
// afficher — d'où les « yADji » à la place de « Doria ».
//
// `profilsPublics/{uid}` = { prenom, avatarCouleur, maj }, lisible par tout
// compte connecté, écrit par son seul propriétaire (cf. règles Firestore).
// Volontairement pauvre : le prénom et rien d'autre, pas d'e-mail.
import { useEffect, useState } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';

// Cache partagé par toute l'app : le même uid revient dans beaucoup d'écrans,
// et un prénom ne change pas au cours d'une session.
const cache = new Map();          // uid → { prenom, avatarCouleur } | null
const enVol = new Map();          // uid → Promise (déduplique les lectures)
const abonnes = new Set();        // callbacks à réveiller quand le cache grossit

function prevenir() { abonnes.forEach(fn => { try { fn(); } catch {} }); }

// Charge les uids manquants. Un uid introuvable est mémorisé à `null` : on ne
// le redemande pas à chaque rendu.
function charger(uids) {
  (uids || []).filter(Boolean).forEach(uid => {
    if (cache.has(uid) || enVol.has(uid)) return;
    const p = getDoc(doc(db, 'profilsPublics', uid))
      .then(snap => { cache.set(uid, snap.exists() ? snap.data() : null); })
      .catch(() => { cache.set(uid, null); })
      .finally(() => { enVol.delete(uid); prevenir(); });
    enVol.set(uid, p);
  });
}

// Résout des uids en noms. Renvoie `nomDe(uid, repli)` :
//   • le prénom public s'il existe ;
//   • sinon le repli fourni par l'appelant (nom figé déjà en base) ;
//   • sinon l'uid tronqué, en dernier recours seulement.
export function useNoms(uids = []) {
  const cle = [...new Set((uids || []).filter(Boolean))].sort().join(',');
  const [, forcer] = useState(0);

  useEffect(() => {
    const reveiller = () => forcer(n => n + 1);
    abonnes.add(reveiller);
    charger(cle ? cle.split(',') : []);
    return () => { abonnes.delete(reveiller); };
  }, [cle]);

  return (uid, repli) => {
    if (!uid) return repli || '';
    const p = cache.get(uid);
    return p?.prenom || repli || String(uid).slice(0, 5);
  };
}

// Écrit/rafraîchit MON profil public. Idempotent : n'écrit que si le contenu a
// changé, pour ne pas produire une écriture à chaque ouverture de l'app.
export async function assurerProfilPublic(uid, { prenom, avatarCouleur } = {}) {
  if (!uid) return;
  try {
    const snap = await getDoc(doc(db, 'profilsPublics', uid));
    const cur = snap.exists() ? snap.data() : null;
    if (cur
      && (cur.prenom || '') === (prenom || '')
      && (cur.avatarCouleur || null) === (avatarCouleur || null)) return;
    const champs = { maj: new Date().toISOString() };
    if (prenom !== undefined) champs.prenom = prenom || '';
    if (avatarCouleur !== undefined) champs.avatarCouleur = avatarCouleur || null;
    await setDoc(doc(db, 'profilsPublics', uid), champs, { merge: true });
    cache.set(uid, { ...(cur || {}), ...champs });
    prevenir();
  } catch {}
}
